import { Ban, Hourglass, MailCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../../app/authContext';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toast';
import { StatusLayout } from '../../layouts/StatusLayout';
import { t } from '../../lib/copy';
import { messageForError } from '../../lib/errors';
import { resendVerification } from './api';

/** Seconds before "Resend email" can be pressed again. */
export const RESEND_COOLDOWN_S = 60;

export function AwaitingApprovalPage() {
  const { user } = useAuth();
  return (
    <StatusLayout
      icon={Hourglass}
      tone="amber"
      title={t('status.pending.title')}
      body={t('status.pending.body')}
    >
      {user && user.emailVerified === false && <VerifyEmailNote email={user.email ?? ''} />}
    </StatusLayout>
  );
}

function VerifyEmailNote({ email }: { email: string }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return;
    const id = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(id);
  }, [wait]);

  const resend = async () => {
    setBusy(true);
    try {
      await resendVerification();
      setResent(true);
      setWait(RESEND_COOLDOWN_S);
    } catch (err) {
      toast({ title: messageForError(err), variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 rounded-card border border-brand/15 bg-brand-50 p-4 text-left text-sm text-brand-700">
      <p className="flex gap-2">
        <MailCheck className="size-5 shrink-0" aria-hidden />
        <span className="break-words">{t('auth.verify.sent', { email })}</span>
      </p>
      {resent && (
        <p role="status" className="mt-2 pl-7">
          {t('auth.verify.resent')}
        </p>
      )}
      <Button
        variant="secondary"
        size="sm"
        className="mt-3 ml-7"
        loading={busy}
        disabled={wait > 0}
        onClick={() => void resend()}
      >
        {wait > 0 ? `${t('auth.verify.resend')} (${wait}s)` : t('auth.verify.resend')}
      </Button>
    </div>
  );
}

export function DeactivatedPage() {
  return (
    <StatusLayout
      icon={Ban}
      tone="red"
      title={t('status.deactivated.title')}
      body={t('status.deactivated.body')}
    />
  );
}
